import { useEffect, useState } from 'react';
import { Button, Card } from 'antd';
import {
  ClipboardDocumentListIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import TextWinds from '../../../components/common/TextWinds';
import AssignmentClassPanel from '../../../components/lms/assignment/status/AssignmentClassPanel';
import { useSearchParams } from 'react-router-dom';
import { openNewWindow } from '../../../api/utils/util-func';
import AssignmentStudentPanel from '../../../components/lms/assignment/status/AssignmentStudentPanel';
import TopButton from '../../../components/common/TopButton';
import { AssignmentStatusType } from '../../../constants/types/lms/type';

export const getProgressRate = (progress: number) => {
  return Math.min(100, Math.max(0, Math.round(progress)));
};

export const getProgressColorClassNames = ({
  progressRate,
  isCompleted,
}: {
  progressRate: number;
  isCompleted: boolean;
}) => {
  if (progressRate <= 0) {
    return {
      barClassName: 'bg-gray3',
      textClassName: 'text-gray6',
    };
  }

  if (isCompleted) {
    return {
      barClassName: 'bg-green-500',
      textClassName: 'text-green-600',
    };
  }

  return {
    barClassName: 'bg-[#0B8ED0]',
    textClassName: '!text-[#0B8ED0]',
  };
};

const STATUS_LABEL_MAP: Partial<Record<AssignmentStatusType, string>> = {
  NOT_STARTED: '미시작',
  IN_PROGRESS: '진행 중',
  COMPLETED: '완료',
  CLOSED: '마감',
};

const STATUS_CLASS_MAP: Partial<Record<AssignmentStatusType, string>> = {
  NOT_STARTED: 'bg-gray1 text-gray7',
  IN_PROGRESS: 'bg-blue-50 !text-[#0B8ED0]',
  COMPLETED: 'bg-green-50 text-green-600',
  CLOSED: 'bg-red-50 text-red-500',
};

export const getStatusLabel = (status: AssignmentStatusType) => {
  return STATUS_LABEL_MAP[status] ?? status;
};

export const getStatusClassName = (status: AssignmentStatusType) => {
  return STATUS_CLASS_MAP[status] ?? 'bg-gray1 text-gray7';
};

export const getTestScoreColorClassName = (score: number) => {
  if (score < 50) return 'text-red-500';
  if (score < 80) return 'text-![#0B8ED0]';
  return 'text-green-600';
};

type AssignmentStatusViewType = 'CLASS' | 'STUDENT';

const assignmentStatusViewOptions: {
  value: AssignmentStatusViewType;
  title: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    value: 'CLASS',
    title: '반별로 보기',
    description: '반 단위로 과제 진행 현황을 확인합니다.',
    icon: <UserGroupIcon className="h-5 w-5 xl:h-7 xl:w-7" />,
  },
  {
    value: 'STUDENT',
    title: '학생별로 보기',
    description: '학생별 제출 여부와 과제 상태를 확인합니다.',
    icon: <ClipboardDocumentListIcon className="h-5 w-5 xl:h-7 xl:w-7" />,
  },
];

/**
 * 개편 LMS (교사) - 과제 관리 > 과제 현황 페이지 > 상단 & 탭 & 콘텐츠
 */
const AssignmentStatus = () => {
  const [_, setSearchParams] = useSearchParams();

  const [viewType, setViewType] = useState<AssignmentStatusViewType>('CLASS');

  useEffect(() => {
    setSearchParams({});
  }, [viewType]);

  return (
    <div className="flex w-full flex-col pt-6 xl:px-5">
      <header className="flex flex-col gap-3 px-5 xl:flex-row xl:items-center xl:justify-between xl:px-0">
        <div className="min-w-0">
          <TextWinds type="title_h2" xlType="title_h3" className="mb-[2px]">
            과제 현황보기
          </TextWinds>

          <TextWinds type="content_body3">
            학생에게 내준 과제의 진행 상태를 확인할 수 있습니다. 반별 또는
            학생별로 과제 제출 현황을 관리해 주세요.
          </TextWinds>
        </div>

        {/* TODO: 페이지별 이용 안내 링크 받으면 주석 제거 예정 */}
        {/* <Button
          type="primary"
          className="shrink-0 rounded-lg !border-purple5 !bg-purple5 px-4 hover:!border-purple6 hover:!bg-purple6"
          onClick={() =>
            openNewWindow(
              'https://jebs-notice.oopy.io/16f768f7-7a39-8042-884c-f4c58f453684',
            )
          }
        >
          이용 안내
        </Button> */}
      </header>

      <div className="my-5 flex flex-col gap-4 px-3 xl:px-0">
        <div className="grid grid-cols-2 gap-3">
          {assignmentStatusViewOptions.map((option) => {
            const isSelected = viewType === option.value;

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setViewType(option.value)}
                className="w-full rounded-xl text-left transition-all duration-200 active:scale-[0.99]"
              >
                <Card
                  className={[
                    'h-full rounded-xl border transition-all duration-200',
                    isSelected
                      ? 'border-purple5 bg-purple1 shadow-sm'
                      : 'border-gray3 bg-white hover:border-purple3 hover:bg-gray1 hover:shadow-sm',
                  ].join(' ')}
                  styles={{
                    body: {
                      padding: 10,
                    },
                  }}
                >
                  <div className="flex items-center gap-2 xl:gap-3">
                    <div
                      className={[
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full xl:h-10 xl:w-10',
                        isSelected
                          ? 'bg-purple5 text-white'
                          : 'bg-gray1 text-gray8',
                      ].join(' ')}
                    >
                      {option.icon}
                    </div>

                    <div className="min-w-0">
                      <TextWinds
                        type="content_body2"
                        className={[
                          'block truncate break-keep font-bold',
                          isSelected ? 'text-purple5' : '',
                        ].join(' ')}
                      >
                        {option.title}
                      </TextWinds>

                      <TextWinds
                        type="caption1"
                        color="gray8"
                        className="mt-0.5 hidden break-keep xl:block"
                      >
                        {option.description}
                      </TextWinds>
                    </div>
                  </div>
                </Card>
              </button>
            );
          })}
        </div>

        <section className="rounded-xl bg-white p-4 shadow-card xl:p-6">
          {viewType === 'CLASS' ? (
            <AssignmentClassPanel />
          ) : (
            <AssignmentStudentPanel />
          )}
        </section>
      </div>

      <TopButton />
    </div>
  );
};

export default AssignmentStatus;
